import React, { forwardRef } from "react";
import HardLink from "@/components/HardLink";
import { Facebook, Instagram, Youtube, Mail, CreditCard } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import logoFull from "@/assets/logo-wordmark.png";

const Footer = forwardRef<HTMLElement, object>((props, ref) => {
  return (
    <footer ref={ref} className="bg-card border-t border-border" {...props}>
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <HardLink 
              to="/" 
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src={logoFull} 
                alt="ALIENJUICE - Premium Auto & Motor Detailing" 
                className="h-10 w-auto"
              />
            </HardLink>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Premium auto en motor detailing producten voor de perfectionist. Out of this world performance voor jouw voertuig - van auto tot motorfiets.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="p-2 bg-secondary rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-all" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/alienjuice.nl/" target="_blank" rel="noopener noreferrer" className="p-2 bg-secondary rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-all" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-secondary rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-all" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="mailto:info@alienjuice.nl" className="p-2 bg-secondary rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/80 transition-all" aria-label="E-mail">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div className="space-y-4">
            <h2 className="font-display text-sm font-semibold text-foreground uppercase tracking-wider">
              Shop
            </h2>
            <ul className="space-y-3">
              {[
                { name: "Exterieur", href: "/shop/exterior" },
                { name: "Interieur", href: "/shop/interior" },
                { name: "Wielen", href: "/shop/wheels" },
                { name: "Bescherming", href: "/shop/protection" },
                { name: "Bundels met korting", href: "/shop?category=bundles" },
                { name: "Alle producten", href: "/shop" },
              ].map((item) => (
                <li key={item.name}>
                  <HardLink to={item.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {item.name}
                  </HardLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Info */}
          <div className="space-y-4">
            <h2 className="font-display text-sm font-semibold text-foreground uppercase tracking-wider">
              Informatie
            </h2>
            <ul className="space-y-3">
              {[
                { name: "Over Alienjuice", href: "/about" },
                { name: "Contact", href: "/contact" },
                { name: "FAQ", href: "/faq" },
                { name: "Blog", href: "/blog" },
                { name: "Detailing Calculator", href: "/tools/detailing-calculator" },
                { name: "Verzenden & Retourneren", href: "/shipping" },
              ].map((item) => (
                <li key={item.name}>
                  <HardLink to={item.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {item.name}
                  </HardLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h2 className="font-display text-sm font-semibold text-foreground uppercase tracking-wider">
              Juridisch
            </h2>
            <ul className="space-y-3">
              {[
                { name: "Privacy Policy", href: "/privacy" },
                { name: "Algemene Voorwaarden", href: "/terms" },
              ].map((item) => (
                <li key={item.name}>
                  <HardLink to={item.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {item.name}
                  </HardLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="max-w-md mx-auto lg:mx-0">
            <NewsletterSignup />
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © 2025 Alienjuice. Alle rechten voorbehouden.
          </p>
          <div className="flex items-center gap-2 text-muted-foreground">
            <CreditCard className="w-5 h-5" />
            <span className="text-xs">iDEAL • Bancontact • Visa • Mastercard</span>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
